import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatCard } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ShieldCheck, ScrollText, Scale, CheckCircle2, XCircle, ShieldAlert,
  Lock, FileClock, Globe, Loader2, ChevronRight, Clock, User, ListFilter,
} from "lucide-react";
import { fmtINR, compactIndian, fmtDate } from "@/lib/format";

export default function Governance() {
  const { user } = useAuth();
  const [crs, setCrs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [tax, setTax] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null);
  const [crFilter, setCrFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a, v, j, t] = await Promise.all([
        api.get("/governance/change-requests"),
        api.get("/governance/audit"),
        api.get("/governance/audit/verify"),
        api.get("/governance/jurisdictions"),
        api.get("/governance/tax-estimate"),
      ]);
      setCrs(c.data); setAudit(a.data); setIntegrity(v.data); setJurisdictions(j.data); setTax(t.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (cr, decision) => {
    setDeciding(cr.id + decision);
    try {
      await api.post(`/governance/change-requests/${cr.id}/decide`, { decision });
      toast.success(decision === "approve" ? "Change approved & applied" : "Change rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not decide"); }
    finally { setDeciding(null); }
  };

  const saveJur = async (j, field, value) => {
    try {
      await api.put("/governance/jurisdictions", { ...j, [field]: Number(value) });
      toast.success(`${j.name} updated`); load();
    } catch { toast.error("Could not update"); }
  };

  const pending = crs.filter((c) => c.status === "pending");
  const crCounts = {
    all: crs.length,
    pending: pending.length,
    approved: crs.filter((c) => c.status === "approved").length,
    rejected: crs.filter((c) => c.status === "rejected").length,
  };
  const visibleCrs = crFilter === "all" ? crs : crs.filter((c) => c.status === crFilter);

  const fmtVal = (k, v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "object") return JSON.stringify(v);
    if (typeof v === "number" && /(value|amount|cost|committed|nav|price|inr)/i.test(k)) return fmtINR(v);
    return String(v);
  };
  const STATUS_BADGE = {
    pending: "bg-[#C9A24B]/15 text-[#D7C09A] border border-[#C9A24B]/30",
    approved: "bg-[#74C2A3]/15 text-[#74C2A3] border border-[#74C2A3]/30",
    rejected: "bg-[#4A1F2A]/40 text-[#E2B4B4] border border-[#4A1F2A]/60",
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Governance & Controls" subtitle="Maker-checker approvals, tamper-evident audit trail, and multi-jurisdiction tax" />
      {loading ? <Skeleton className="h-96 rounded-xl" /> : (
        <Tabs defaultValue="approvals">
          <TabsList className="mb-5">
            <TabsTrigger value="approvals" data-testid="tab-approvals"><ShieldCheck className="h-4 w-4 mr-2" /> Approvals {pending.length > 0 && <span className="ml-1.5 rounded-full bg-[var(--accent-brass)] text-white text-[10px] px-1.5">{pending.length}</span>}</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="h-4 w-4 mr-2" /> Audit Trail</TabsTrigger>
            <TabsTrigger value="jurisdictions" data-testid="tab-jurisdictions"><Scale className="h-4 w-4 mr-2" /> Jurisdictions & Tax</TabsTrigger>
          </TabsList>

          {/* Approvals */}
          <TabsContent value="approvals">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <StatCard label="Pending" value={crCounts.pending} icon={Clock} accent="#C9A24B" testid="cr-stat-pending" />
              <StatCard label="Approved" value={crCounts.approved} icon={CheckCircle2} accent="#74C2A3" />
              <StatCard label="Rejected" value={crCounts.rejected} icon={XCircle} accent="#C58B7A" />
            </div>

            <div className="mb-4 flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1.5 text-xs text-[color:var(--mk-muted)]"><ListFilter className="h-3.5 w-3.5" /> Filter</span>
              {["all", "pending", "approved", "rejected"].map((f) => (
                <Button key={f} size="sm" variant={crFilter === f ? "default" : "outline"}
                  onClick={() => setCrFilter(f)} data-testid={`cr-filter-${f}`}
                  className={crFilter === f ? "bg-[var(--primary-deep-green)] text-white hover:bg-[var(--primary-deep-green-hover)] capitalize" : "border-[color:var(--mk-border-strong)] bg-transparent text-[color:var(--mk-text-2)] hover:bg-white/5 capitalize"}>
                  {f} ({crCounts[f]})
                </Button>
              ))}
            </div>

            {visibleCrs.length === 0 ? <Card className="bg-white border-border"><EmptyState icon={ShieldCheck} title={crFilter === "all" ? "No change requests" : `No ${crFilter} requests`} description="Sensitive changes submitted through maker-checker appear here for a second authoriser to approve or reject. A maker can never approve their own request." /></Card>
              : <div className="space-y-3" data-testid="change-requests-list">{visibleCrs.map((cr) => {
                const isOpen = expanded === cr.id;
                const payload = cr.payload || {};
                const keys = Object.keys(payload);
                return (
                <Card key={cr.id} className="bg-white border-border overflow-hidden" data-testid={`cr-card-${cr.id}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[var(--ink)]">{cr.summary}</span>
                          <Badge variant="secondary" className="capitalize">{cr.resource_type.replace("_", " ")}</Badge>
                          <Badge variant="secondary" className="capitalize">{cr.action}</Badge>
                          <Badge className={STATUS_BADGE[cr.status]}>{cr.status[0].toUpperCase() + cr.status.slice(1)}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> Maker: <span className="font-medium text-[color:var(--mk-text-2)]">{cr.maker_name}</span></span>
                          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDate(cr.created_at)}</span>
                          {cr.checker_name && <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Checker: <span className="font-medium text-[color:var(--mk-text-2)]">{cr.checker_name}</span></span>}
                        </div>
                      </div>
                      {cr.status === "pending" && (
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="outline" className="text-[#E2B4B4] border-[#4A1F2A]/60 hover:bg-[#4A1F2A]/20" onClick={() => decide(cr, "reject")} disabled={!!deciding} data-testid={`reject-${cr.id}`}>{deciding === cr.id + "reject" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><XCircle className="h-4 w-4 mr-1" /> Reject</>}</Button>
                          <Button size="sm" className="bg-[var(--primary-deep-green)] hover:bg-[var(--primary-deep-green-hover)] text-white" onClick={() => decide(cr, "approve")} disabled={!!deciding} data-testid={`approve-${cr.id}`}>{deciding === cr.id + "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</>}</Button>
                        </div>
                      )}
                    </div>

                    {cr.maker_id === user?.id && cr.status === "pending" && (
                      <p className="text-[11px] text-[#D7C09A] mt-2 flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" /> You raised this request — a different authoriser must approve it.</p>
                    )}
                    {cr.comment && <p className="text-xs text-muted-foreground mt-2 italic">Checker note: “{cr.comment}”</p>}

                    {keys.length > 0 && (
                      <button onClick={() => setExpanded(isOpen ? null : cr.id)} data-testid={`cr-toggle-${cr.id}`}
                        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[color:var(--mk-primary-2)] hover:text-[color:var(--mk-primary)] focus-brass rounded">
                        <ChevronRight className={"h-3.5 w-3.5 transition-transform " + (isOpen ? "rotate-90" : "")} />
                        {isOpen ? "Hide" : "Review"} proposed changes ({keys.length})
                      </button>
                    )}
                  </div>

                  {isOpen && keys.length > 0 && (
                    <div className="border-t border-border bg-[color:var(--mk-surface-2)] px-4 py-3" data-testid={`cr-payload-${cr.id}`}>
                      <div className="text-[11px] uppercase tracking-wider text-[color:var(--mk-muted)] mb-2">Proposed {cr.action} payload</div>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                        {keys.map((k) => (
                          <div key={k} className="flex items-baseline justify-between gap-3 border-b border-border/50 py-1">
                            <dt className="text-xs text-[color:var(--mk-muted)] capitalize">{k.replace(/_/g, " ")}</dt>
                            <dd className="text-xs font-data text-[color:var(--mk-text)] text-right truncate max-w-[60%]">{fmtVal(k, payload[k])}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </Card>
              ); })}</div>}
          </TabsContent>

          {/* Audit */}
          <TabsContent value="audit">
            <Card className={"border p-4 mb-4 flex items-center gap-3 " + (integrity?.valid ? "bg-[#0E7A5F]/[0.05] border-[#0E7A5F]/25" : "bg-[#B42318]/[0.05] border-[#B42318]/25")} data-testid="audit-integrity">
              {integrity?.valid ? <Lock className="h-5 w-5 text-[#0E7A5F]" /> : <ShieldAlert className="h-5 w-5 text-[#B42318]" />}
              <div>
                <div className={"text-sm font-semibold " + (integrity?.valid ? "text-[#0E7A5F]" : "text-[#B42318]")}>{integrity?.valid ? "Audit chain verified — tamper-evident & intact" : `Integrity breach detected at entry #${integrity?.broken_at}`}</div>
                <div className="text-xs text-muted-foreground">{integrity?.count ?? audit.length} hash-chained entries (SHA-256)</div>
              </div>
            </Card>
            {audit.length === 0 ? <Card className="bg-white border-border"><EmptyState icon={FileClock} title="No audit entries yet" description="Every sensitive action (creates, edits, approvals, logins) is recorded in an immutable hash-chained ledger." /></Card>
              : <Card className="bg-white border-border overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm" data-testid="audit-table"><thead className="bg-[var(--sand)]/60"><tr className="text-left text-xs font-semibold text-slate-600"><th className="px-4 py-3">#</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">When</th><th className="px-4 py-3">Hash</th></tr></thead>
                <tbody>{audit.map((e) => (<tr key={e.id} className="border-t border-border hover:bg-[var(--sand)]/40">
                  <td className="px-4 py-2.5 font-data text-slate-500">{e.seq}</td>
                  <td className="px-4 py-2.5"><Badge variant="secondary" className="font-data text-[11px]">{e.payload?.action}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-600">{e.payload?.actor_name || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{fmtDate(e.payload?.at)}</td>
                  <td className="px-4 py-2.5 font-data text-[11px] text-slate-400">{(e.hash || "").slice(0, 16)}…</td>
                </tr>))}</tbody>
              </table></div></Card>}
          </TabsContent>

          {/* Jurisdictions */}
          <TabsContent value="jurisdictions">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <StatCard label="Jurisdictions" value={jurisdictions.length} icon={Globe} accent="#0F3D2E" />
              <StatCard label="Est. LT CG Tax (unrealised)" value={"₹" + compactIndian(tax?.total_estimated_tax_inr)} icon={Scale} accent="#B45309" testid="tax-total" />
              <StatCard label="Tax Jurisdictions in Use" value={tax?.by_jurisdiction?.length || 0} icon={ShieldCheck} accent="#123A63" />
            </div>

            <Card className="bg-white border-border overflow-hidden mb-4">
              <div className="p-5 pb-2"><h3 className="text-sm font-semibold text-[var(--ink)]">Tax Rule Sets (editable)</h3><p className="text-xs text-muted-foreground mt-0.5">Capital-gains rates by jurisdiction used for tax estimation on holdings.</p></div>
              <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="jurisdictions-table"><thead className="bg-[var(--sand)]/60"><tr className="text-left text-xs font-semibold text-slate-600"><th className="px-4 py-3">Jurisdiction</th><th className="px-4 py-3">Currency</th><th className="px-4 py-3">FY Start</th><th className="px-4 py-3 text-right">LT CG Rate %</th><th className="px-4 py-3 text-right">ST CG Rate %</th><th className="px-4 py-3 text-right">LT Threshold (mo)</th></tr></thead>
                <tbody>{jurisdictions.map((j) => (<tr key={j.code} className="border-t border-border">
                  <td className="px-4 py-2.5 font-medium text-[var(--ink)]"><Badge variant="secondary" className="mr-2 font-data">{j.code}</Badge>{j.name}</td>
                  <td className="px-4 py-2.5 text-slate-600">{j.currency}</td>
                  <td className="px-4 py-2.5 text-slate-600">{["", "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][j.fy_start_month]}</td>
                  <td className="px-4 py-2 text-right"><Input type="number" defaultValue={j.lt_cg_rate} className="w-24 ml-auto font-data text-right h-8" onBlur={(e) => Number(e.target.value) !== j.lt_cg_rate && saveJur(j, "lt_cg_rate", e.target.value)} data-testid={`jur-lt-${j.code}`} /></td>
                  <td className="px-4 py-2 text-right"><Input type="number" defaultValue={j.st_cg_rate} className="w-24 ml-auto font-data text-right h-8" onBlur={(e) => Number(e.target.value) !== j.st_cg_rate && saveJur(j, "st_cg_rate", e.target.value)} /></td>
                  <td className="px-4 py-2.5 text-right font-data text-slate-600">{j.lt_threshold_months}</td>
                </tr>))}</tbody>
              </table></div>
            </Card>

            {tax?.by_jurisdiction?.length > 0 && (
              <Card className="bg-white border-border overflow-hidden">
                <div className="p-5 pb-2"><h3 className="text-sm font-semibold text-[var(--ink)]">Estimated Tax on Unrealised Gains</h3></div>
                <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="tax-estimate-table"><thead className="bg-[var(--sand)]/60"><tr className="text-left text-xs font-semibold text-slate-600"><th className="px-4 py-3">Jurisdiction</th><th className="px-4 py-3 text-right">Unrealised Gain (INR)</th><th className="px-4 py-3 text-right">LT CG Rate</th><th className="px-4 py-3 text-right">Estimated Tax (INR)</th></tr></thead>
                  <tbody>{tax.by_jurisdiction.map((g, i) => (<tr key={i} className="border-t border-border"><td className="px-4 py-2.5 font-medium">{g.jurisdiction}</td><td className="px-4 py-2.5 text-right font-data tabular-nums">{fmtINR(g.unrealised_gain_inr)}</td><td className="px-4 py-2.5 text-right font-data">{g.lt_cg_rate}%</td><td className="px-4 py-2.5 text-right font-data tabular-nums font-semibold text-[#B45309]">{fmtINR(g.estimated_tax_inr)}</td></tr>))}</tbody>
                </table></div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
